/**
 * MobileDetection - Device detection and orientation management
 *
 * Provides utilities for detecting mobile devices and managing screen orientation
 * for optimal gameplay experience.
 */

/**
 * Device type detection result
 */
export enum DeviceType {
  DESKTOP = 'desktop',
  MOBILE = 'mobile',
  TABLET = 'tablet',
}

/**
 * Screen orientation
 */
export enum ScreenOrientation {
  PORTRAIT = 'portrait',
  LANDSCAPE = 'landscape',
}

/**
 * Mobile detection result
 */
export interface MobileDetectionResult {
  /** Device type */
  deviceType: DeviceType;
  /** Whether this is a touch-capable device */
  isTouchDevice: boolean;
  /** Whether this is a mobile phone or tablet */
  isMobileOrTablet: boolean;
  /** Current screen orientation */
  orientation: ScreenOrientation;
  /** Whether the screen is small (phone-sized) */
  isSmallScreen: boolean;
  /** Device pixel ratio */
  devicePixelRatio: number;
}

/**
 * Detect device type based on user agent and screen characteristics
 */
export function detectDevice(): MobileDetectionResult {
  const ua = navigator.userAgent.toLowerCase();

  // Check for touch capability
  const isTouchDevice = 'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0;

  // Check for mobile user agents
  const mobileKeywords = [
    'android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry',
    'windows phone', 'opera mini', 'mobile', 'tablet'
  ];
  const isMobileUA = mobileKeywords.some(keyword => ua.includes(keyword));

  // Check for tablet-specific patterns
  const isTabletUA = ua.includes('ipad') ||
    (ua.includes('android') && !ua.includes('mobile')) ||
    ua.includes('tablet');

  // Screen size checks
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const minDimension = Math.min(screenWidth, screenHeight);

  // Phone: < 768px shortest dimension
  // Tablet: 768-1024px shortest dimension
  const isSmallScreen = minDimension < 768;
  const isTabletSize = minDimension >= 768 && minDimension <= 1024;

  // Determine device type
  let deviceType: DeviceType;
  if (isTabletUA || (isTouchDevice && isTabletSize && !isSmallScreen)) {
    deviceType = DeviceType.TABLET;
  } else if (isMobileUA || (isTouchDevice && isSmallScreen)) {
    deviceType = DeviceType.MOBILE;
  } else {
    deviceType = DeviceType.DESKTOP;
  }

  // Determine orientation
  const orientation = window.innerWidth > window.innerHeight
    ? ScreenOrientation.LANDSCAPE
    : ScreenOrientation.PORTRAIT;

  return {
    deviceType,
    isTouchDevice,
    isMobileOrTablet: deviceType !== DeviceType.DESKTOP,
    orientation,
    isSmallScreen,
    devicePixelRatio: window.devicePixelRatio || 1,
  };
}

/**
 * Check if device is mobile or tablet
 */
export function isMobileOrTablet(): boolean {
  return detectDevice().isMobileOrTablet;
}

/**
 * Check if device has touch support
 */
export function isTouchDevice(): boolean {
  return detectDevice().isTouchDevice;
}

/**
 * Get current screen orientation
 */
export function getOrientation(): ScreenOrientation {
  return window.innerWidth > window.innerHeight
    ? ScreenOrientation.LANDSCAPE
    : ScreenOrientation.PORTRAIT;
}

/**
 * Try to lock screen to landscape orientation
 * Returns true if lock was successful or not needed
 */
export async function lockToLandscape(): Promise<boolean> {
  // Check if Screen Orientation API is available
  // Note: screen.orientation is a browser API, not our enum
  const hasOrientationAPI = 'orientation' in screen && screen.orientation !== null;

  if (hasOrientationAPI && 'lock' in screen.orientation) {
    try {
      await (screen.orientation as any).lock('landscape');
      return true;
    } catch (error) {
      // Lock failed - might not be in fullscreen, or not supported
      return false;
    }
  }

  // API not available
  return false;
}

/**
 * Unlock screen orientation
 */
export function unlockOrientation(): void {
  if (screen.orientation && 'unlock' in screen.orientation) {
    try {
      (screen.orientation as any).unlock();
    } catch (error) {
      // Unlock failed - silently ignore
    }
  }
}

/**
 * Add orientation change listener
 * Returns cleanup function
 */
export function onOrientationChange(callback: (orientation: ScreenOrientation) => void): () => void {
  const handler = () => {
    callback(getOrientation());
  };

  // Use both resize and orientationchange for broader support
  window.addEventListener('resize', handler);
  window.addEventListener('orientationchange', handler);

  return () => {
    window.removeEventListener('resize', handler);
    window.removeEventListener('orientationchange', handler);
  };
}

/**
 * Request fullscreen for better mobile experience
 * Fullscreen is required for orientation lock on many devices
 */
export async function requestFullscreen(element?: HTMLElement): Promise<boolean> {
  const target = element || document.documentElement;

  try {
    if (target.requestFullscreen) {
      await target.requestFullscreen();
      return true;
    } else if ((target as any).webkitRequestFullscreen) {
      await (target as any).webkitRequestFullscreen();
      return true;
    } else if ((target as any).mozRequestFullScreen) {
      await (target as any).mozRequestFullScreen();
      return true;
    } else if ((target as any).msRequestFullscreen) {
      await (target as any).msRequestFullscreen();
      return true;
    }
  } catch (error) {
    // Fullscreen request failed - silently ignore
  }

  return false;
}

/**
 * Exit fullscreen
 */
export async function exitFullscreen(): Promise<void> {
  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen();
    } else if ((document as any).webkitExitFullscreen) {
      await (document as any).webkitExitFullscreen();
    } else if ((document as any).mozCancelFullScreen) {
      await (document as any).mozCancelFullScreen();
    } else if ((document as any).msExitFullscreen) {
      await (document as any).msExitFullscreen();
    }
  } catch (error) {
    // Exit fullscreen failed - silently ignore
  }
}

/**
 * Check if currently in fullscreen mode
 */
export function isFullscreen(): boolean {
  return !!(
    document.fullscreenElement ||
    (document as any).webkitFullscreenElement ||
    (document as any).mozFullScreenElement ||
    (document as any).msFullscreenElement
  );
}

/**
 * MobileDeviceManager - Singleton for managing mobile device state
 */
export class MobileDeviceManager {
  private static _instance: MobileDeviceManager | null = null;

  private _deviceInfo: MobileDetectionResult;
  private _orientationListeners: Set<(orientation: ScreenOrientation) => void> = new Set();
  private _cleanupFn: (() => void) | null = null;

  private constructor() {
    this._deviceInfo = detectDevice();
    this._setupListeners();
  }

  static getInstance(): MobileDeviceManager {
    if (!MobileDeviceManager._instance) {
      MobileDeviceManager._instance = new MobileDeviceManager();
    }
    return MobileDeviceManager._instance;
  }

  private _setupListeners(): void {
    this._cleanupFn = onOrientationChange((orientation) => {
      this._deviceInfo = detectDevice();
      this._orientationListeners.forEach(listener => listener(orientation));
    });
  }

  /**
   * Get current device info
   */
  get deviceInfo(): MobileDetectionResult {
    return this._deviceInfo;
  }

  /**
   * Check if current device is mobile or tablet
   */
  get isMobile(): boolean {
    return this._deviceInfo.isMobileOrTablet;
  }

  /**
   * Check if touch is available
   */
  get hasTouch(): boolean {
    return this._deviceInfo.isTouchDevice;
  }

  /**
   * Get current orientation
   */
  get orientation(): ScreenOrientation {
    return this._deviceInfo.orientation;
  }

  /**
   * Check if in landscape mode
   */
  get isLandscape(): boolean {
    return this._deviceInfo.orientation === ScreenOrientation.LANDSCAPE;
  }

  /**
   * Check if in portrait mode
   */
  get isPortrait(): boolean {
    return this._deviceInfo.orientation === ScreenOrientation.PORTRAIT;
  }

  /**
   * Add orientation change listener
   */
  addOrientationListener(callback: (orientation: ScreenOrientation) => void): void {
    this._orientationListeners.add(callback);
  }

  /**
   * Remove orientation change listener
   */
  removeOrientationListener(callback: (orientation: ScreenOrientation) => void): void {
    this._orientationListeners.delete(callback);
  }

  /**
   * Initialize mobile mode (fullscreen + landscape lock)
   */
  async enterMobileMode(): Promise<boolean> {
    if (!this.isMobile) return true;

    // Request fullscreen first (required for orientation lock)
    const fullscreenSuccess = await requestFullscreen();

    if (fullscreenSuccess) {
      // Try to lock to landscape
      await lockToLandscape();
      return true;
    }

    return false;
  }

  /**
   * Exit mobile mode
   */
  async exitMobileMode(): Promise<void> {
    unlockOrientation();
    await exitFullscreen();
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this._cleanupFn) {
      this._cleanupFn();
      this._cleanupFn = null;
    }
    this._orientationListeners.clear();
    MobileDeviceManager._instance = null;
  }
}
